// MobileWatchList.jsx
// Phone-tier Watch List — scoped-pills + instrument rows, bound to the REAL
// watchlist data/handlers from WatchList.jsx. Native touch affordances:
//   • swipe a row left  → reveal Trade + Remove actions
//   • ⋯ (manage lists)  → rename / delete current list, or create a new one
//   • +                 → add instrument (opens the shared selector modal)
// Theme-aware (DS vars), mono tabular numbers.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sheet, SwipeRow, ListRow, TAP, PullToRefresh } from "../ds";

const mono = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontVariantNumeric: "tabular-nums",
};

const fmtPrice = (n) =>
  n == null || n === ""
    ? "—"
    : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "EQUITY", label: "Equity" },
  { value: "FUT", label: "Futures" },
  { value: "OPT", label: "Options" },
];

// variant="accent" (default) is the screen's ONE accent-filled pill — reserved
// for the group pill. variant="neutral" is the recessed, Segmented-like
// treatment for type/scope filters: active = --bg-3 fill + --fg-1 text,
// inactive = transparent + hairline border + --fg-3 text. No accent.
function Pill({ active, children, onClick, variant = "accent" }) {
  const neutral = variant === "neutral";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: "none",
        cursor: "pointer",
        flex: "0 0 auto",
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
        padding: "5px 12px",
        borderRadius: 999,
        color: neutral
          ? active ? "var(--fg-1)" : "var(--fg-3)"
          : active ? "var(--fg-on-accent)" : "var(--fg-2)",
        background: neutral
          ? active ? "var(--bg-3)" : "transparent"
          : active ? "var(--color-accent)" : "var(--bg-2)",
        border: `1px solid ${neutral ? "var(--border)" : active ? "var(--color-accent)" : "var(--border)"}`,
      }}
    >
      {children}
    </button>
  );
}

const TradeGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /></svg>
);
const TrashGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
);

// A single instrument row: DS ListRow face wrapped in a DS SwipeRow that
// reveals Trade + Remove on swipe-left.
function WatchRow({ inst, last, onTrade, onRemove }) {
  const chg = inst.change_value ?? 0;
  const pct = inst.change_percent ?? 0;
  const hasLtp = inst.ltp != null && inst.ltp !== "";
  const up = chg >= 0;
  const color = !hasLtp ? "var(--fg-3)" : up ? "var(--color-profit)" : "var(--color-loss)";
  const sub = inst.name || inst.instrument_type || inst.exchange || "";

  return (
    <SwipeRow
      style={last ? undefined : { borderBottom: "1px solid var(--border)" }}
      actions={[
        { key: "trade", label: "Trade", tone: "accent", icon: TradeGlyph, onClick: () => onTrade(inst) },
        { key: "remove", label: "Remove", tone: "danger", icon: TrashGlyph, onClick: () => onRemove(inst) },
      ]}
    >
      <ListRow
        onClick={() => onTrade(inst)}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>{inst.symbol}</span>
            <span style={{ fontSize: 9, fontWeight: 600, color: "var(--fg-3)", border: "1px solid var(--border)", padding: "1px 5px", borderRadius: 3, letterSpacing: ".05em", flex: "0 0 auto" }}>
              {inst.exchange}
            </span>
          </span>
        }
        subtitle={sub}
        trailing={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <span style={{ ...mono, fontSize: 13, fontWeight: 500, color: "var(--fg-1)" }}>
              {hasLtp ? `₹${fmtPrice(inst.ltp)}` : "—"}
            </span>
            <span style={{ ...mono, fontSize: 11, color }}>
              {hasLtp ? `${up ? "+" : "−"}${fmtPrice(Math.abs(chg))} (${up ? "+" : "−"}${Math.abs(pct).toFixed(2)}%)` : "No quote"}
            </span>
          </div>
        }
      />
    </SwipeRow>
  );
}

// Bottom sheet for managing lists: rename current, delete current, create new.
function ListManageSheet({ open, group, onRename, onDelete, onCreate, onClose }) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  // Re-seed the rename field whenever the sheet opens for a group.
  React.useEffect(() => {
    if (open && group) { setName(group.name || ""); setCreating(false); setNewName(""); }
  }, [open, group]);

  return (
    <Sheet open={open} onClose={onClose} title="Manage list">
            {/* Rename current */}
            <label style={{ fontSize: 10, color: "var(--fg-3)", letterSpacing: ".10em", textTransform: "uppercase", fontWeight: 700 }}>List name</label>
            <div style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 18 }}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="List name"
                style={{
                  flex: 1, height: 44, borderRadius: 10, padding: "0 12px",
                  background: "var(--bg-3)", border: "1px solid var(--border)",
                  color: "var(--fg-1)", fontSize: 14, outline: "none",
                }}
              />
              <button
                type="button"
                disabled={!name.trim() || !group}
                onClick={() => { onRename(group.id, name.trim()); onClose(); }}
                style={{
                  appearance: "none", cursor: "pointer", border: "none", borderRadius: 10, padding: "0 16px",
                  background: "var(--color-accent)", color: "var(--fg-on-accent)", fontWeight: 700, fontSize: 14,
                  opacity: (!name.trim() || !group) ? 0.4 : 1,
                }}
              >
                Save
              </button>
            </div>

            {/* Create new list */}
            {creating ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <input
                  autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="New list name"
                  style={{ flex: 1, height: 44, borderRadius: 10, padding: "0 12px", background: "var(--bg-3)", border: "1px solid var(--border)", color: "var(--fg-1)", fontSize: 14, outline: "none" }}
                />
                <button
                  type="button" disabled={!newName.trim()}
                  onClick={() => { onCreate(newName.trim()); onClose(); }}
                  style={{ appearance: "none", cursor: "pointer", border: "1px solid var(--border)", borderRadius: 10, padding: "0 16px", background: "var(--bg-3)", color: "var(--fg-1)", fontWeight: 600, fontSize: 14, opacity: newName.trim() ? 1 : 0.4 }}
                >
                  Create
                </button>
              </div>
            ) : (
              <button
                type="button" onClick={() => setCreating(true)}
                style={{ appearance: "none", width: "100%", cursor: "pointer", textAlign: "left", padding: "13px 14px", background: "var(--bg-3)", borderRadius: 12, border: "1px solid var(--border)", color: "var(--fg-1)", fontSize: 14, fontWeight: 500, marginBottom: 14 }}
              >
                + New list
              </button>
            )}

            {/* Delete current — hands off to a NAMED confirmation (onDelete does
                NOT delete directly; the parent opens a confirm modal). */}
            <button
              type="button" disabled={!group}
              onClick={() => { onDelete(group.id); onClose(); }}
              style={{ appearance: "none", width: "100%", cursor: "pointer", textAlign: "left", padding: "13px 14px", background: "var(--bg-3)", borderRadius: 12, border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, opacity: group ? 1 : 0.4 }}
            >
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 500, color: "var(--danger)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Delete “{group?.name}”
                </span>
                <span style={{ display: "block", fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
                  Removes the list and its instruments
                </span>
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
            </button>
    </Sheet>
  );
}

export default function MobileWatchList({
  instruments = [],
  groups = [],
  activeGroupId,
  onSelectGroup,
  filterType = "ALL",
  onFilterType,
  onAdd,
  onRemove,
  onRenameGroup,
  onDeleteGroup,
  onCreateGroup,
  onRefresh,
}) {
  const navigate = useNavigate();
  const [manageOpen, setManageOpen] = useState(false);
  const activeGroup = groups.find((g) => g.id === activeGroupId) || null;

  // Pull-to-refresh over the instrument list. The phone AppShell owns
  // document-level scrolling, so PTR engages off window scrollTop === 0. No
  // dedicated refetch handler exists on the current props, so fall back to a
  // short resolve until the parent threads one in.
  const handleRefresh =
    onRefresh || (() => new Promise((res) => setTimeout(res, 600))); // TODO: thread real watchlist refetch from WatchList.jsx

  const IconBtn = ({ label, onClick, children }) => (
    <motion.button
      type="button" aria-label={label} onClick={onClick} whileTap={TAP}
      style={{
        appearance: "none", cursor: "pointer", flex: "0 0 auto", width: 32, height: 32,
        borderRadius: 10, background: "var(--bg-2)", color: "var(--fg-2)",
        border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {children}
    </motion.button>
  );

  return (
    <div style={{ padding: "0 16px", color: "var(--fg-1)" }}>
      {/* Group pills + manage (⋯) + add (+) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, paddingBottom: 2 }}>
          {groups.map((g) => (
            <Pill key={g.id} active={g.id === activeGroupId} onClick={() => onSelectGroup?.(g.id)}>
              {g.name}
            </Pill>
          ))}
        </div>
        {(onRenameGroup || onCreateGroup) && (
          <IconBtn label="Manage lists" onClick={() => setManageOpen(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
          </IconBtn>
        )}
        {onAdd && (
          <motion.button
            type="button" aria-label="Add instrument" onClick={onAdd} whileTap={TAP}
            style={{ appearance: "none", cursor: "pointer", flex: "0 0 auto", width: 32, height: 32, borderRadius: 10, background: "rgba(var(--color-accent-rgb), 0.14)", color: "var(--color-accent)", border: "1px solid var(--border)", fontSize: 20, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            +
          </motion.button>
        )}
      </div>

      {/* Type filter */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 12 }}>
        {TYPE_FILTERS.map((t) => (
          <Pill key={t.value} active={filterType === t.value} variant="neutral" onClick={() => onFilterType?.(t.value)}>
            {t.label}
          </Pill>
        ))}
      </div>

      {/* List — wrapped in PullToRefresh (drag down at page-top to refetch). */}
      <PullToRefresh onRefresh={handleRefresh}>
      {instruments.length === 0 ? (
        <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, padding: "40px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No instruments</div>
          <div style={{ fontSize: 12, color: "var(--fg-3)", marginBottom: 16 }}>This watchlist is empty.</div>
          {onAdd && (
            <button type="button" onClick={onAdd} style={{ appearance: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--fg-on-accent)", background: "var(--color-accent)", border: "none", borderRadius: 999, padding: "8px 16px" }}>
              + Add instrument
            </button>
          )}
        </div>
      ) : (
        <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
          {instruments.map((inst, i) => (
            <WatchRow
              key={inst.instrument_id || inst.id || i}
              inst={inst}
              last={i === instruments.length - 1}
              /* MUST pass the instrument in router state: SmartTrade reads
                 state.instrument and redirects straight back to /WatchList
                 without it, so the tap looked like a no-op. Mirrors the desktop
                 rows (WatchListRow.jsx:17 / WatchListRowCompact.jsx:16). */
              onTrade={(it) => navigate("/SmartTrade", { state: { instrument: it } })}
              onRemove={(it) => onRemove?.(it.instrument_id)}
            />
          ))}
        </div>
      )}
      </PullToRefresh>

      <div style={{ textAlign: "center", marginTop: 14, fontSize: 10, color: "var(--fg-3)", letterSpacing: ".03em" }}>
        Swipe a row left for Trade · Remove
      </div>

      <ListManageSheet
        open={manageOpen}
        group={activeGroup}
        onRename={(id, nm) => onRenameGroup?.(id, nm)}
        onDelete={(id) => onDeleteGroup?.(id)}
        onCreate={(nm) => onCreateGroup?.(nm)}
        onClose={() => setManageOpen(false)}
      />
    </div>
  );
}
